import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, shareReplay, switchMap, tap } from 'rxjs/operators';

import { Auth0Service } from '@geneplanet/ngx-auth0';
import { environment } from 'src/environments/environment';

import { CustomerCheckService } from '../../shared/services/customer-check.service';
import { AncestryService } from '../../ancestry/services/ancestry.service';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  Profile,
  FullProfile,
  Country,
  Kit,
} from '../interfaces/me.interfaces';
import { IntentService } from 'src/app/shared/services/intent/intent.service';
import { IntentStrategyService } from 'src/app/shared/services/intent/intent-strategy.service';
import { SideMenuService } from 'src/app/core/services/side-menu.service';
import { Router } from '@angular/router';
import { AnalyticsService } from '@geneplanet/ngx-utils/src/lib/analytics';
import { OnboardingService } from 'src/app/onboarding/onboarding.service';
import { CreateCustomerPayload } from 'src/app/onboarding/interfaces/onboarding.interface';

@Injectable({
  providedIn: 'root',
})
export class MeService {
  private http = inject(HttpClient);
  private readonly auth0 = inject(Auth0Service);
  private readonly customerCheckService = inject(CustomerCheckService);
  private readonly ancestryService = inject(AncestryService);
  private readonly builder = inject(UntypedFormBuilder);
  private readonly intentService = inject(IntentService);
  private readonly intentStrategyService = inject(IntentStrategyService);
  private sideMenuService = inject(SideMenuService);
  private analyticsService = inject(AnalyticsService);
  private readonly onboardingService = inject(OnboardingService);
  private readonly router = inject(Router);

  public isNiptUser = new BehaviorSubject(false);
  public profile$: Observable<FullProfile> | null = null;
  form!: UntypedFormGroup;

  buildForm() {
    this.form = this.builder.group({
      address: this.builder.group({
        street: ['', Validators.required],
        post: ['', Validators.required],
        city: ['', Validators.required],
        country: ['', Validators.required],
      }),
      unitSystem: ['', Validators.compose([])],
    });
    return this.form;
  }

  updateForm(me: FullProfile) {
    this.form.setValue({
      address: {
        street: me.address.street,
        post: me.address.post,
        city: me.address.city,
        country: me.address.country,
      },
      unitSystem: me?.unitSystem ? me.unitSystem : 'Metric',
    });
  }

  logout(): void {
    this.sideMenuService.clearCategories();
    this.clearProfileCache();
    this.analyticsService.reset();
    this.auth0.logout();
    this.intentService.clear();
    this.intentStrategyService.resetStrategy();
    this.intentService.inIntent = false;
  }

  clearProfileCache() {
    this.ancestryService.cleanup();
    this.onboardingService.customer =
      null as unknown as Partial<CreateCustomerPayload>;
    this.profile$ = null;
  }

  getFullProfile(forceUpdate = false): Observable<FullProfile> | null {
    if (forceUpdate) {
      // Reset to make new API call. Required when your profile changes
      this.profile$ = null;
    }

    if (!this.profile$) {
      return this.customerCheckService.customerExists$.pipe(
        filter((customerExists: boolean) => !!customerExists),
        switchMap(() => this.fetchProfile())
      );
    }
    return this.profile$;
  }

  getSubscriptionUrl(id: string) {
    return this.http.post<any>(
      `${environment.payment.stripeUrl}/customer_portal`,
      {
        subscriptionCustomerId: id,
        returnUrl: `${environment.dashboardUrl}me`,
      }
    );
  }

  saveProfile(profile: Profile): Observable<Profile> {
    return this.http.put<Profile>(`${environment.api}/me`, profile);
  }

  countries(): Observable<Country[]> {
    return this.http.get<Country[]>(`${environment.api}/countries`);
  }

  getKits(): Observable<Kit[]> {
    return this.http.get<any>(`${environment.api}/customers/kits`);
  }

  saveConsent(consent: boolean): Observable<any> {
    return this.http.put(`/api/consents/SalivaConsent`, {
      value: consent,
    });
  }

  deleteAccount(reason: string): Observable<any> {
    return this.http.post(`${environment.api}/request-deprovision`, { reason });
  }

  private fetchProfile(): Observable<FullProfile> {
    this.profile$ = this.http.get<FullProfile>(`${environment.api}/me`).pipe(
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
      tap((profile) => {
        this.isNiptUser.next(!!profile.niptUser);

        if (profile.markedForDeletion) {
          this.logout();
          this.router.navigate(['/accounts/check']);
        }
      })
    );
    return this.profile$;
  }
}
